import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PageHeaderComponent } from '@shared/components/layout';
import { CellEditSaveEvent, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { NotificationService } from '@shared/services/notification.service';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import {
  OrganizationUnitEventTypeDto,
  OrganizationUnitEventTypeManagementService,
  UpdateOrganizationUnitEventTypeDto,
} from '../../services/organization-unit-event-type-management.service';

@Component({
  selector: 'app-organization-unit-event-type-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    PageHeaderComponent,
    TableSurfaceComponent,
  ],
  templateUrl: './organization-unit-event-type-list.component.html',
  styleUrl: './organization-unit-event-type-list.component.scss',
})
export class OrganizationUnitEventTypeListComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [
    { key: 'name', header: 'Nombre', hideable: true, visible: true, resizable: true, editable: true, editType: 'text' },
    { key: 'description', header: 'Descripcion', hideable: true, visible: true, resizable: true, editable: true, editType: 'text' },
    { key: 'actions', header: 'Acciones', width: '1%', minWidth: '104px', exportable: false },
  ];

  tableData: OrganizationUnitEventTypeDto[] = [];
  isLoading = false;
  isDragDropEnabled = true;
  savingCell?: { row: OrganizationUnitEventTypeDto; column: string };
  searchTerm = '';
  allTypes: OrganizationUnitEventTypeDto[] = [];

  private readonly destroy$ = new Subject<void>();
  private isSaving = false;

  constructor(
    private readonly service: OrganizationUnitEventTypeManagementService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTypes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createNew(): void {
    this.router.navigate(['/configuration/community-event-types/new']);
  }

  editType(type: OrganizationUnitEventTypeDto): void {
    this.router.navigate(['/configuration/community-event-types', type.value, 'edit']);
  }

  deleteType(type: OrganizationUnitEventTypeDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar tipo de evento',
        message: `Estas seguro de que deseas eliminar el tipo "${type.name}"?`,
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.confirmDelete(type.value);
        }
      });
  }

  onToolbarSearch(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  onRowsReordered(event: { data: OrganizationUnitEventTypeDto[] }): void {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    const orderedIds = event.data.map(item => item.value);

    this.service.updateSortOrder(orderedIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.allTypes = [...event.data];
          this.notificationService.success('Orden actualizado exitosamente');
          this.isSaving = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationService.error('Error al actualizar el orden. Recargando...');
          this.isSaving = false;
          this.loadTypes();
          this.cdr.markForCheck();
        },
      });
  }

  onCellEditSave(event: CellEditSaveEvent<OrganizationUnitEventTypeDto>): void {
    if (!['name', 'description'].includes(event.column)) {
      return;
    }

    const newValue = String(event.newValue ?? '').trim();
    const oldValue = String(event.oldValue ?? '');

    if (event.column === 'name' && !newValue) {
      this.notificationService.warning('El nombre no puede quedar vacio');
      return;
    }

    const type = event.row;
    this.savingCell = { row: type, column: event.column };
    this.cdr.markForCheck();

    const updateDto: UpdateOrganizationUnitEventTypeDto = {
      name: event.column === 'name' ? newValue : type.name,
      description: event.column === 'description' ? newValue || null : type.description,
      sortOrder: type.sortOrder,
    };

    this.service.update(type.value, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (event.column === 'name') {
            type.name = newValue;
          } else {
            type.description = newValue;
          }
          this.tableData = [...this.tableData];
          this.notificationService.success('Tipo de evento actualizado exitosamente');
          this.savingCell = undefined;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating event type:', error);
          if (event.column === 'name') {
            type.name = oldValue;
          } else {
            type.description = oldValue;
          }
          this.notificationService.error(error?.error?.message || error?.error?.title || 'Error al actualizar el tipo de evento');
          this.savingCell = undefined;
          this.loadTypes();
          this.cdr.markForCheck();
        },
      });
  }

  private loadTypes(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: types => {
          this.allTypes = types;
          this.applyFilter();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationService.error('Error al cargar los tipos de eventos');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private confirmDelete(id: number): void {
    this.service.delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Tipo de evento eliminado exitosamente');
          this.loadTypes();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.notificationService.error(error?.error?.message || error?.error?.title || 'Error al eliminar el tipo de evento');
          this.cdr.markForCheck();
        },
      });
  }

  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const filteredData = term
      ? this.allTypes.filter(type =>
          type.name.toLowerCase().includes(term) ||
          (type.description ?? '').toLowerCase().includes(term)
        )
      : this.allTypes;

    this.tableData = [...filteredData];
    this.cdr.markForCheck();
  }
}
